import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { Utils, useBreakpointValue } from '@genial/utils';
import { RouteProp, useRoute } from '@react-navigation/native';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useToastCreator } from '../../../hooks/useToastCreator';
import { SignedInRootStackParamList } from '../../../routes/types';
import { basketCustomerOrderResumeState } from '../../../store/batchTicket/atom';
import { getCustomerBalance } from '../services';
import { calculateAllocatedBalance, validatePercentage } from '../utils';
import { errorBalanceUpdateMessage } from '../utils/customer';

export const useBasketDetailsInfoResume = () => {
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'BatchTicketResumeDetail'>>();
  const { basketCustomerOrderId, type } = params;

  const basketCustomerOrderResume = useRecoilValue(basketCustomerOrderResumeState);

  const basketCustomerOrderItem = basketCustomerOrderResume.data.all.find(
    (item) => item.basketCustomerOrderId === basketCustomerOrderId
  );

  const basketCustomerOrderErrorsItem = basketCustomerOrderResume.data.errors.find(
    (item) => item.basketCustomerOrderId === basketCustomerOrderId
  );

  const basketCustomerOrderValidItem = basketCustomerOrderResume.data.valid.find(
    (item) => item.basketCustomerOrderId === basketCustomerOrderId
  );

  const basketCustomerOrderValidItemCount = basketCustomerOrderValidItem?.customerOffers?.reduce(
    (acc, current) => {
      return acc + (current?.basketOffers?.length ?? 0);
    },
    0
  );

  const basketCustomerOrderErrosItemCount = basketCustomerOrderErrorsItem?.customerOffers?.reduce(
    (acc, current) => {
      return acc + (current?.basketOffers?.length ?? 0);
    },
    0
  );

  const { showToast, showToastFromError } = useToastCreator('toast-detail-resume-basket');

  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(
    basketCustomerOrderItem?.customer?.account?.availableBalance || 0
  );

  const isMobile = useBreakpointValue({ base: true, sm: false });

  const routeParamTypeIsTotalSummary = type === 'totalSummary';

  const assetQuantityInfo = routeParamTypeIsTotalSummary
    ? { label: 'Ativos adicion.', value: `${basketCustomerOrderValidItemCount} Ativos` }
    : { label: 'Ativos divergentes', value: `${basketCustomerOrderErrosItemCount} Ativos` };

  const handleUpdateBalance = async () => {
    try {
      setIsLoadingBalance(true);

      const availableBalanceResponse = await getCustomerBalance(
        basketCustomerOrderItem?.customer?.account?.accountNumber!
      );

      if (availableBalanceResponse !== availableBalance) {
        setAvailableBalance(availableBalanceResponse);
      }

      showToast(...errorBalanceUpdateMessage, { duration: 3000 });
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorBalanceUpdateMessage);
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const allocatedValidBalanceOffers = calculateAllocatedBalance(
    basketCustomerOrderValidItem?.customerOffers || []
  );

  const allocatedInvalidBalanceOffers = calculateAllocatedBalance(
    basketCustomerOrderErrorsItem?.customerOffers || []
  );

  const allocatedBalance = routeParamTypeIsTotalSummary
    ? allocatedValidBalanceOffers
    : allocatedInvalidBalanceOffers;

  const allocatedBalanceFormatted = Utils.Mask.Money(allocatedBalance);

  const remainingBalance = availableBalance - allocatedBalance;

  const remainingBalanceFormatted = Utils.Mask.Money(remainingBalance);

  const allocatedBalancePercentage = (allocatedBalance / availableBalance) * 100;

  const allocatedBalancePercentageFormatted = Utils.Mask.Percentage(
    validatePercentage(allocatedBalancePercentage)
  );

  const remainingBalancePercentage = (remainingBalance / availableBalance) * 100;

  const remainingBalancePercentageFormatted = Utils.Mask.Percentage(
    validatePercentage(remainingBalancePercentage)
  );

  const allocatedBalanceField = `${allocatedBalanceFormatted} | ${allocatedBalancePercentageFormatted}`;
  const remainingBalanceField = `${remainingBalanceFormatted} | ${remainingBalancePercentageFormatted}`;

  return {
    isMobile,
    assetQuantityInfo,
    routeParamTypeIsTotalSummary,
    basketCustomerOrderItem,
    handleUpdateBalance,
    isLoadingBalance,
    availableBalance,
    allocatedBalanceField,
    remainingBalanceField,
  };
};
